import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';

function GenerationOverlay({ title, subtitle, duration, steps, onComplete }) {
  const [progress, setProgress] = useState(0);
  const completedRef = useRef(false);
  const startedAt = useRef(Date.now());
  const safeSteps = useMemo(() => steps?.length ? steps : ['正在准备任务', 'AI 正在计算', '正在整理结果'], [steps]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      const elapsed = Date.now() - startedAt.current;
      const next = Math.min(100, Math.round((elapsed / duration) * 100));
      setProgress(next);
      if (next >= 100 && !completedRef.current) {
        completedRef.current = true;
        window.clearInterval(tick);
        window.setTimeout(onComplete, 260);
      }
    }, 100);
    return () => window.clearInterval(tick);
  }, [duration, onComplete]);

  const activeStep = Math.min(safeSteps.length - 1, Math.floor((progress / 100) * safeSteps.length));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#07080b]/90 px-4 backdrop-blur-xl">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-white/10 bg-[#15171d] shadow-2xl shadow-blue-950/40">
        <div className="relative p-8 md:p-10">
          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-blue-600/20 blur-3xl" />
          <div className="relative">
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-400">
              <Sparkles className="h-7 w-7 animate-pulse" />
            </div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.24em] text-blue-400">Chuangshi AI Engine</p>
            <h2 className="text-2xl font-semibold text-white md:text-3xl">{title}</h2>
            <p className="mt-2 text-sm leading-6 text-gray-400">{subtitle}</p>

            <div className="mt-8 h-2 overflow-hidden rounded-full bg-white/10">
              <div className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-violet-500 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            <div className="mt-2 flex justify-between text-xs text-gray-500">
              <span>{safeSteps[activeStep]}</span>
              <span>{progress}%</span>
            </div>

            <div className="mt-7 space-y-3">
              {safeSteps.map((step, index) => {
                const done = index < activeStep || progress === 100;
                const active = index === activeStep && progress < 100;
                return (
                  <div key={step} className={`flex items-center gap-3 text-sm ${done ? 'text-gray-300' : active ? 'text-white' : 'text-gray-600'}`}>
                    <span className={`flex h-6 w-6 items-center justify-center rounded-full ${done ? 'bg-emerald-500/20 text-emerald-400' : active ? 'bg-blue-500/20 text-blue-400' : 'bg-white/5'}`}>
                      {done ? <Check className="h-3.5 w-3.5" /> : active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : index + 1}
                    </span>
                    {step}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GenerationOverlay;
